import PDFDocument from 'pdfkit/js/pdfkit.standalone';
import biteHeaderLight from '../../resources/img/biteHeader.png';
import { fetchFont } from './EntityTicketPdf';
import {
  IBeneficialOwnershipVisualData,
  IVisualLabels,
  IVisualNetworkGroup,
  IVisualNetworkItem,
  IVisualText,
  VisualTone,
} from 'utils/beneficialOwnershipVisualData';

// The one-page visual summary of a saved Beneficial Ownership investigation,
// on the dark navy BITE dashboard theme. What it is for is the network map and
// the sanctions exposure: who the subject is connected to, and which of those
// parties are sanctioned. Everything it shows is read from the saved report
// (utils/beneficialOwnershipVisualData); this file only lays it out:
//
//   - the network map leads, with the sanctions exposure beside it: the
//     verdict, every flagged party and its list, the high-risk jurisdictions;
//   - the top findings run as a short strip underneath;
//   - a section with nothing to show is left out, and the page is only as
//     tall as its content needs.
//
// The visual is only offered when the report has a network to map
// (hasNetworkToMap). Every text call is width- and line-capped, so long names
// are cut with an ellipsis instead of spilling onto a second page.

const PAGE_W = 1224; // Tabloid width, landscape
// The sample dashboard is 1200 x 987; a full report grows to about that.
const MAX_PAGE_H = 990;
const M = 32;
const CONTENT_W = PAGE_W - 2 * M;
const STRIP_Y = 116;
const STRIP_H = 78;
const KPI_H = 58;
const ROW_GAP = 12;
const PANEL_GAP = 14;
const FOOTER_H = 46;
const MIN_MAIN_H = 260;

const COLOR = {
  page: '#0B111E',
  card: '#121A2B',
  cardBorder: '#1F2A40',
  node: '#161F33',
  divider: '#1F2A40',
  text: '#F1F5F9',
  secondary: '#A9B4C6',
  muted: '#6E7C93',
  label: '#8C99AE',
  accent: '#38BDF8',
  blue: '#4F8EF7',
  ring: '#3B82F6',
  connector: '#2E4A7A',
  subject: '#0E1730',
};

const TONE: {
  [tone in VisualTone]: {
    text: string;
    solid: string;
    bg: string;
    border: string;
  };
} = {
  high: { text: '#F87171', solid: '#EF4444', bg: '#2A1520', border: '#7A2330' },
  medium: {
    text: '#FBBF24',
    solid: '#F59E0B',
    bg: '#2A2113',
    border: '#6B4E16',
  },
  low: { text: '#4ADE80', solid: '#22C55E', bg: '#10261B', border: '#1E5A3A' },
  info: { text: '#60A5FA', solid: '#3B82F6', bg: '#0F1B33', border: '#1E3A6B' },
};

interface IFonts {
  regular: string;
  bold: string;
}

/** The Beneficial Ownership wording; another tool's visual overrides it. */
const BO_LABELS: IVisualLabels = {
  kicker: 'Risk analysis · Beneficial ownership investigation',
  subtitle:
    'Uncovering hidden ownership, sanctioned connections and network risks',
  strip: [
    'Subject entity',
    'Investigation result',
    'BITE analysis',
    'Report generated',
  ],
  networkTitle: 'Network map',
  networkItem: ['party', 'parties'],
  networkSource: 'mapped from the report',
  moreGroup: 'More connections',
  compactGroup: 'Connections',
  exposureTitle: 'Sanctions exposure',
  exposureItem: ['flagged party', 'flagged parties'],
  exposureNone: 'No flagged parties',
  exposureList: 'Sanctioned & flagged parties',
  exposureEmpty: 'No sanctioned or flagged parties were found in the network.',
  exposureMore: 'more in the network map and report',
  jurisdictions: 'High-risk jurisdictions',
  summary: 'Sanctions summary',
  footer: 'BITE · Beneficial Ownership Intelligence · bitedata.io',
  documentTitle: 'BITE Beneficial Ownership Visual Summary',
  documentAuthor: 'BITE Beneficial Ownership',
};

const labelsOf = (data: IBeneficialOwnershipVisualData): IVisualLabels => ({
  ...BO_LABELS,
  ...(data.labels || {}),
});

const nounOf = (n: number, noun: [string, string]): string =>
  `${n.toLocaleString('en-US')} ${n === 1 ? noun[0] : noun[1]}`;

/**
 * The tone an item is drawn in: its own when the visual grades its items,
 * else high for a flagged item. Null is a plain, unflagged item.
 */
const itemTone = (item: IVisualNetworkItem): VisualTone | null =>
  item.tone && item.tone !== 'info' ? item.tone : item.flagged ? 'high' : null;

const registerFonts = (
  doc: any,
  regular: ArrayBuffer | null,
  bold: ArrayBuffer | null,
): IFonts => {
  const fallback = { regular: 'Helvetica', bold: 'Helvetica-Bold' };
  if (!regular || !bold) return fallback;
  try {
    doc.registerFont('Body', new Uint8Array(regular));
    doc.registerFont('BodyBold', new Uint8Array(bold));
    // PDFKit opens embedded fonts lazily; resolve now so a bad file falls back.
    doc.font('Body');
    doc.font('BodyBold');
    return { regular: 'Body', bold: 'BodyBold' };
  } catch {
    return fallback;
  }
};

/** The light-text logo, made for dark backgrounds. */
const loadLightLogo = async (): Promise<ArrayBuffer | string | null> => {
  try {
    if (
      typeof biteHeaderLight === 'string' &&
      biteHeaderLight.startsWith('data:')
    ) {
      return biteHeaderLight;
    }
    const res = await fetch(biteHeaderLight);
    if (res.ok) return await res.arrayBuffer();
  } catch {
    /* the logo is optional */
  }
  return null;
};

const clean = (value: any): string =>
  String(value ?? '')
    .replace(/[\u0000-\u001F\u007F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export interface ITextOpts {
  width: number;
  font: string;
  size: number;
  color: string;
  maxLines?: number;
  lineGap?: number;
  align?: 'left' | 'center' | 'right';
  spacing?: number;
}

const textOptions = (opts: ITextOpts) => ({
  width: opts.width,
  lineGap: opts.lineGap || 0,
  align: opts.align || 'left',
  characterSpacing: opts.spacing || 0,
});

const lineHeightOf = (doc: any, opts: ITextOpts): number => {
  doc.font(opts.font).fontSize(opts.size);
  return doc.currentLineHeight(true) + (opts.lineGap || 0);
};

/** The height `drawText` would take for this text, without drawing it. */
const measure = (doc: any, value: string, opts: ITextOpts): number => {
  const str = clean(value);
  if (!str) return 0;
  const maxHeight = (opts.maxLines || 1) * lineHeightOf(doc, opts);
  return Math.min(doc.heightOfString(str, textOptions(opts)), maxHeight);
};

/**
 * Draws text cut to `maxLines` (default one) with an ellipsis and returns the
 * height it took.
 */
const drawText = (
  doc: any,
  value: string,
  x: number,
  y: number,
  opts: ITextOpts,
): number => {
  const str = clean(value);
  if (!str) return 0;
  const used = measure(doc, str, opts);
  doc.fillColor(opts.color);
  doc.text(str, x, y, {
    ...textOptions(opts),
    height: (opts.maxLines || 1) * lineHeightOf(doc, opts),
    ellipsis: true,
  });
  return used;
};

const box = (
  doc: any,
  x: number,
  y: number,
  w: number,
  h: number,
  fill: string,
  stroke: string,
  radius = 8,
) => {
  doc.save();
  doc
    .lineWidth(0.8)
    .roundedRect(x, y, w, h, radius)
    .fillAndStroke(fill, stroke);
  doc.restore();
};

const hLine = (doc: any, x1: number, x2: number, y: number) => {
  doc.save();
  doc
    .strokeColor(COLOR.divider)
    .lineWidth(0.8)
    .moveTo(x1, y)
    .lineTo(x2, y)
    .stroke();
  doc.restore();
};

const glow = (
  doc: any,
  cx: number,
  cy: number,
  inner: number,
  outer: number,
  opacity: number,
) => {
  const gradient = doc.radialGradient(cx, cy, inner, cx, cy, outer);
  gradient.stop(0, COLOR.ring, opacity).stop(1, COLOR.ring, 0);
  doc.save();
  doc.circle(cx, cy, outer).fill(gradient);
  doc.restore();
};

const label = (
  doc: any,
  fonts: IFonts,
  value: string,
  x: number,
  y: number,
  width: number,
  color = COLOR.label,
  align: 'left' | 'center' | 'right' = 'left',
) =>
  drawText(doc, value.toUpperCase(), x, y, {
    width: width,
    font: fonts.bold,
    size: 7,
    color: color,
    spacing: 1,
    align: align,
  });

const PANEL_HEAD = 46;
const PANEL_PAD = 18;

const panel = (
  doc: any,
  fonts: IFonts,
  title: string,
  subtitle: string,
  x: number,
  y: number,
  w: number,
  h: number,
) => {
  box(doc, x, y, w, h, COLOR.card, COLOR.cardBorder, 10);
  drawText(doc, title, x + PANEL_PAD, y + 16, {
    width: w - 2 * PANEL_PAD,
    font: fonts.bold,
    size: 13,
    color: COLOR.text,
  });
  if (subtitle) {
    doc.font(fonts.bold).fontSize(13);
    const titleW = doc.widthOfString(clean(title));
    drawText(doc, subtitle, x + PANEL_PAD + titleW + 10, y + 20, {
      width: Math.max(0, w - 2 * PANEL_PAD - titleW - 10),
      font: fonts.regular,
      size: 8.5,
      color: COLOR.muted,
    });
  }
};

// ---- Header, strip and counts ----------------------------------------------

const renderHeader = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  logo: ArrayBuffer | string | null,
  pageH: number,
) => {
  doc.save();
  doc.rect(0, 0, PAGE_W, pageH).fill(COLOR.page);
  doc.restore();
  glow(doc, PAGE_W * 0.62, 34, 0, 230, 0.16);

  let titleX = M;
  if (logo) {
    try {
      doc.image(logo, M, 40, { width: 108 });
      doc.save();
      doc
        .strokeColor(COLOR.divider)
        .lineWidth(0.8)
        .moveTo(M + 128, 28)
        .lineTo(M + 128, 86)
        .stroke();
      doc.restore();
      titleX = M + 146;
    } catch {
      /* the logo is optional */
    }
  }

  const verdictW = 300;
  const titleW = PAGE_W - M - verdictW - 24 - titleX;
  const labels = labelsOf(data);
  label(doc, fonts, labels.kicker, titleX, 28, titleW, COLOR.accent);
  drawText(doc, data.entityName, titleX, 40, {
    width: titleW,
    font: fonts.bold,
    size: 26,
    color: COLOR.text,
  });
  drawText(doc, labels.subtitle, titleX, 78, {
    width: titleW,
    font: fonts.regular,
    size: 9.5,
    color: COLOR.secondary,
  });

  const tone = TONE[data.verdict.tone];
  const vx = PAGE_W - M - verdictW;
  box(doc, vx, 30, verdictW, 50, tone.bg, tone.border, 8);
  drawText(doc, data.verdict.subject.toUpperCase(), vx + 16, 41, {
    width: verdictW - 32,
    font: fonts.bold,
    size: 8.5,
    color: tone.text,
    spacing: 0.8,
    align: 'right',
  });
  drawText(doc, data.verdict.network.toUpperCase(), vx + 16, 56, {
    width: verdictW - 32,
    font: fonts.bold,
    size: 10,
    color: tone.solid,
    spacing: 0.8,
    align: 'right',
  });

  hLine(doc, M, PAGE_W - M, 102);
};

const renderStrip = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  y: number,
) => {
  const strip = labelsOf(data).strip;
  const columns: Array<{ label: string; content: IVisualText }> = [
    { label: strip[0], content: data.subject },
    { label: strip[1], content: data.ownershipResult },
    { label: strip[2], content: data.analysis },
    { label: strip[3], content: data.report },
  ];
  const w = (CONTENT_W - ROW_GAP * (columns.length - 1)) / columns.length;
  columns.forEach((column, index) => {
    const x = M + index * (w + ROW_GAP);
    box(doc, x, y, w, STRIP_H, COLOR.card, COLOR.cardBorder, 10);
    label(doc, fonts, column.label, x + 16, y + 13, w - 32);
    const titleH = drawText(doc, column.content.title, x + 16, y + 26, {
      width: w - 32,
      font: fonts.bold,
      size: 11,
      color:
        column.content.tone === 'info'
          ? COLOR.text
          : TONE[column.content.tone].text,
    });
    drawText(doc, column.content.body, x + 16, y + 30 + titleH, {
      width: w - 32,
      font: fonts.regular,
      size: 8,
      color: COLOR.secondary,
      maxLines: 2,
      lineGap: 1.5,
    });
  });
};

const renderKpis = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  y: number,
) => {
  const h = KPI_H;
  const w = (CONTENT_W - ROW_GAP * (data.kpis.length - 1)) / data.kpis.length;
  data.kpis.forEach((kpi, index) => {
    const x = M + index * (w + ROW_GAP);
    const color = kpi.tone === 'info' ? COLOR.blue : TONE[kpi.tone].solid;
    box(doc, x, y, w, h, COLOR.card, COLOR.cardBorder, 10);
    doc.save();
    doc.roundedRect(x, y, 3, h, 1.5).fill(color);
    doc.restore();
    doc.font(fonts.bold).fontSize(26);
    const value = kpi.value.toLocaleString('en-US');
    const valueW = Math.max(30, doc.widthOfString(value));
    drawText(doc, value, x + 18, y + (h - 34) / 2, {
      width: valueW + 4,
      font: fonts.bold,
      size: 26,
      color: color,
    });
    const textX = x + 18 + valueW + 14;
    label(
      doc,
      fonts,
      kpi.label,
      textX,
      y + h / 2 - 11,
      x + w - 14 - textX,
      COLOR.secondary,
    );
    drawText(doc, kpi.detail, textX, y + h / 2 + 1, {
      width: x + w - 14 - textX,
      font: fonts.regular,
      size: 8,
      color: COLOR.muted,
    });
  });
};

// ---- Key findings strip ----------------------------------------------------
// The top findings as a short strip of cards under the map. The full list
// stays in the report.

const STRIP_FINDINGS = 3;
const FINDING_CARD_PAD = 14;

const findingCardOpts = (fonts: IFonts, cardW: number) => {
  const textW = cardW - 2 * FINDING_CARD_PAD - 28;
  return {
    title: {
      width: textW,
      font: fonts.bold,
      size: 9.5,
      color: COLOR.text,
      maxLines: 2,
    } as ITextOpts,
    body: {
      width: cardW - 2 * FINDING_CARD_PAD,
      font: fonts.regular,
      size: 8,
      color: COLOR.secondary,
      maxLines: 3,
      lineGap: 1.5,
    } as ITextOpts,
  };
};

const stripFindings = (data: IBeneficialOwnershipVisualData) =>
  data.findings.slice(0, STRIP_FINDINGS);

const findingCardW = (count: number): number =>
  (CONTENT_W - ROW_GAP * (count - 1)) / count;

/** The strip's height: its label row and its tallest card. */
const needFindingsStrip = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
): number => {
  const shown = stripFindings(data);
  if (!shown.length) return 0;
  const opts = findingCardOpts(fonts, findingCardW(shown.length));
  const tallest = Math.max(
    ...shown.map(
      (finding) =>
        Math.max(18, measure(doc, finding.title, opts.title)) +
        6 +
        measure(doc, finding.body, opts.body),
    ),
  );
  return 18 + 2 * FINDING_CARD_PAD + tallest;
};

const renderFindingsStrip = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  y: number,
  h: number,
) => {
  const shown = stripFindings(data);
  const hidden = data.findings.length - shown.length;
  label(
    doc,
    fonts,
    hidden
      ? `Key findings · top ${shown.length} of ${data.findings.length}`
      : `Key findings · ${shown.length}`,
    M,
    y,
    CONTENT_W / 2,
  );
  if (hidden) {
    drawText(
      doc,
      `+${hidden.toLocaleString('en-US')} more in the full report`,
      M + CONTENT_W / 2,
      y - 0.5,
      {
        width: CONTENT_W / 2,
        font: fonts.bold,
        size: 7.5,
        color: COLOR.accent,
        align: 'right',
      },
    );
  }

  const cardW = findingCardW(shown.length);
  const opts = findingCardOpts(fonts, cardW);
  const cardY = y + 18;
  shown.forEach((finding, index) => {
    const x = M + index * (cardW + ROW_GAP);
    const tone = TONE[finding.tone];
    box(doc, x, cardY, cardW, h - 18, COLOR.card, COLOR.cardBorder, 10);
    box(
      doc,
      x + FINDING_CARD_PAD,
      cardY + FINDING_CARD_PAD,
      18,
      18,
      tone.bg,
      tone.border,
      4,
    );
    drawText(
      doc,
      String(index + 1),
      x + FINDING_CARD_PAD,
      cardY + FINDING_CARD_PAD + 4.5,
      {
        width: 18,
        font: fonts.bold,
        size: 8,
        color: tone.text,
        align: 'center',
      },
    );
    const textX = x + FINDING_CARD_PAD + 28;
    if (finding.level) {
      drawText(
        doc,
        finding.level.toUpperCase(),
        x + cardW - FINDING_CARD_PAD - 60,
        cardY + FINDING_CARD_PAD + 1,
        {
          width: 60,
          font: fonts.bold,
          size: 7,
          color: tone.text,
          spacing: 0.8,
          align: 'right',
        },
      );
    }
    const titleH = drawText(
      doc,
      finding.title,
      textX,
      cardY + FINDING_CARD_PAD,
      {
        ...opts.title,
        width: opts.title.width - (finding.level ? 56 : 0),
      },
    );
    drawText(
      doc,
      finding.body,
      x + FINDING_CARD_PAD,
      cardY + FINDING_CARD_PAD + Math.max(18, titleH) + 6,
      opts.body,
    );
  });
};

/**
 * The findings as a column beside the exposure, for a small network: as many
 * as fit the row, highest risk first, then "+N more in the full report".
 */
const renderFindingsColumn = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  x: number,
  y: number,
  w: number,
  h: number,
) => {
  panel(
    doc,
    fonts,
    'Key findings',
    `${data.findings.length.toLocaleString('en-US')} in the report`,
    x,
    y,
    w,
    h,
  );
  const innerX = x + PANEL_PAD;
  const innerW = w - 2 * PANEL_PAD;
  const opts = findingCardOpts(fonts, w - 2 * PANEL_PAD + 2 * FINDING_CARD_PAD);
  const bottom = y + h - PANEL_PAD;
  const moreRoom = 18;
  let cy = y + PANEL_HEAD + 2;

  let shown = 0;
  for (let i = 0; i < data.findings.length; i += 1) {
    const finding = data.findings[i];
    const titleW = opts.title.width - (finding.level ? 56 : 0);
    const titleOpts = { ...opts.title, width: titleW };
    const itemH =
      Math.max(18, measure(doc, finding.title, titleOpts)) +
      6 +
      measure(doc, finding.body, opts.body);
    const left = data.findings.length - i - 1;
    if (cy + itemH > bottom - (left ? moreRoom : 0)) break;

    const tone = TONE[finding.tone];
    box(doc, innerX, cy, 18, 18, tone.bg, tone.border, 4);
    drawText(doc, String(i + 1), innerX, cy + 4.5, {
      width: 18,
      font: fonts.bold,
      size: 8,
      color: tone.text,
      align: 'center',
    });
    if (finding.level) {
      drawText(doc, finding.level.toUpperCase(), innerX + innerW - 60, cy + 1, {
        width: 60,
        font: fonts.bold,
        size: 7,
        color: tone.text,
        spacing: 0.8,
        align: 'right',
      });
    }
    const titleH = drawText(doc, finding.title, innerX + 28, cy, titleOpts);
    drawText(
      doc,
      finding.body,
      innerX,
      cy + Math.max(18, titleH) + 6,
      opts.body,
    );
    cy += itemH + 14;
    shown += 1;
    if (left) hLine(doc, innerX, innerX + innerW, cy - 7);
  }

  const hidden = data.findings.length - shown;
  if (hidden > 0) {
    drawText(
      doc,
      `+${hidden.toLocaleString('en-US')} more in the full report`,
      innerX,
      bottom - 11,
      { width: innerW, font: fonts.bold, size: 8, color: COLOR.accent },
    );
  }
};

// ---- Network map -----------------------------------------------------------

const COLUMN_W = 150;
const NODE_H = 32;
const NODE_GAP = 7;
const GRID_GAP = 8;
const SUBJECT_R = 52;
const COLUMN_REACH = 110; // most room between a column and the subject
const MAP_TOP = 58; // below the panel header
const COLUMN_MAX = 9; // nodes a column asks room for
const COLUMN_MAX_WITH_GRID = 7;
const GRID_MAX_ROWS = 3;

const gridColumnsFor = (innerW: number): number =>
  Math.max(2, Math.min(6, Math.floor((innerW + GRID_GAP) / (116 + GRID_GAP))));

const drawNode = (
  doc: any,
  fonts: IFonts,
  item: IVisualNetworkItem,
  x: number,
  y: number,
  w: number,
) => {
  const toneKey = itemTone(item);
  const tone = toneKey ? TONE[toneKey] : null;
  box(
    doc,
    x,
    y,
    w,
    NODE_H,
    tone ? tone.bg : COLOR.node,
    tone ? tone.border : COLOR.cardBorder,
    6,
  );
  doc.save();
  doc.circle(x + 10, y + 11.5, 2.6).fill(tone ? tone.solid : COLOR.ring);
  doc.restore();
  drawText(doc, item.name, x + 18, y + 6, {
    width: w - 24,
    font: fonts.bold,
    size: 8,
    color: COLOR.text,
  });
  drawText(doc, item.detail, x + 18, y + 18, {
    width: w - 24,
    font: fonts.regular,
    size: 6.8,
    color: tone ? tone.text : COLOR.muted,
  });
};

const drawMore = (
  doc: any,
  fonts: IFonts,
  hidden: number,
  x: number,
  y: number,
  w: number,
) => {
  doc.save();
  doc
    .lineWidth(0.8)
    .dash(3, { space: 2 })
    .roundedRect(x, y, w, NODE_H, 6)
    .stroke(COLOR.cardBorder);
  doc.undash();
  doc.restore();
  drawText(doc, `+${hidden.toLocaleString('en-US')} more`, x, y + 11, {
    width: w,
    font: fonts.bold,
    size: 8,
    color: COLOR.accent,
    align: 'center',
  });
};

const connect = (
  doc: any,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  tone: VisualTone | null,
  vertical = false,
) => {
  doc.save();
  doc
    .strokeColor(tone ? TONE[tone].solid : COLOR.connector)
    .strokeOpacity(tone ? 0.6 : 0.9)
    .lineWidth(0.9)
    .moveTo(fromX, fromY);
  if (vertical) {
    const midY = (fromY + toY) / 2;
    doc.bezierCurveTo(fromX, midY, toX, midY, toX, toY);
  } else {
    const midX = (fromX + toX) / 2;
    doc.bezierCurveTo(midX, fromY, midX, toY, toX, toY);
  }
  doc.stroke();
  doc.restore();
};

const groupLabel = (group: IVisualNetworkGroup): string =>
  `${group.title} · ${group.items.length.toLocaleString('en-US')}`;

/** How many of a group fit in `slots`, keeping the last for "+N more". */
const fitted = (group: IVisualNetworkGroup, slots: number) => {
  if (group.items.length <= slots) {
    return { items: group.items, hidden: 0 };
  }
  const shown = Math.max(0, slots - 1);
  return {
    items: group.items.slice(0, shown),
    hidden: group.items.length - shown,
  };
};

// More than three groups: the third slot holds the rest, together.
const toSlots = (
  groups: IVisualNetworkGroup[],
  moreTitle: string,
): IVisualNetworkGroup[] => {
  if (groups.length <= 3) return groups;
  const rest = groups.slice(2);
  const items = rest.reduce(
    (all: IVisualNetworkItem[], group) => all.concat(group.items),
    [],
  );
  return groups.slice(0, 2).concat({
    key: 'other',
    title: moreTitle,
    items: items
      .filter((item) => item.flagged)
      .concat(items.filter((item) => !item.flagged)),
    flaggedCount: rest.reduce((sum, group) => sum + group.flaggedCount, 0),
  });
};

// A network this small is not worth a full map: it is drawn compactly, as
// one grid of connections under the subject, and the room goes to findings.
const COMPACT_NETWORK_MAX = 6;

export const isCompactNetwork = (
  data: IBeneficialOwnershipVisualData,
): boolean => data.network.total <= COMPACT_NETWORK_MAX;

/** The groups the map draws: one merged grid when the network is small. */
const slotsFor = (
  data: IBeneficialOwnershipVisualData,
): IVisualNetworkGroup[] => {
  const groups = data.network.groups;
  const labels = labelsOf(data);
  if (!isCompactNetwork(data) || groups.length <= 1) {
    return toSlots(groups, labels.moreGroup);
  }
  const items = groups.reduce(
    (all: IVisualNetworkItem[], group) => all.concat(group.items),
    [],
  );
  return [
    {
      key: 'other',
      title: labels.compactGroup,
      items: items
        .filter((item) => item.flagged)
        .concat(items.filter((item) => !item.flagged)),
      flaggedCount: groups.reduce((sum, group) => sum + group.flaggedCount, 0),
    },
  ];
};

const stackHeight = (count: number): number =>
  count ? count * NODE_H + (count - 1) * NODE_GAP : 0;

/** Room a column beside the subject asks for: its label and nodes. */
const columnNeed = (group: IVisualNetworkGroup | undefined, max: number) =>
  group ? 16 + stackHeight(Math.min(group.items.length, max)) : 0;

/** Room a grid under the subject asks for: its label and rows. */
const gridNeed = (group: IVisualNetworkGroup, cols: number): number => {
  const rows = Math.min(GRID_MAX_ROWS, Math.ceil(group.items.length / cols));
  return 26 + 22 + rows * NODE_H + (rows - 1) * GRID_GAP;
};

const SUBJECT_ROOM = 2 * SUBJECT_R + 40;

const needNetwork = (
  data: IBeneficialOwnershipVisualData,
  w: number,
): number => {
  const slots = slotsFor(data);
  const cols = gridColumnsFor(w - 2 * PANEL_PAD);
  if (slots.length === 1) {
    return MAP_TOP + SUBJECT_ROOM - 20 + gridNeed(slots[0], cols) + PANEL_PAD;
  }
  const max = slots.length === 3 ? COLUMN_MAX_WITH_GRID : COLUMN_MAX;
  const upper = Math.max(
    columnNeed(slots[0], max),
    columnNeed(slots[1], max),
    SUBJECT_ROOM,
  );
  return (
    MAP_TOP +
    upper +
    (slots.length === 3 ? gridNeed(slots[2], cols) : 0) +
    PANEL_PAD
  );
};

/** A vertical column of nodes beside the subject, wired to it. */
const renderColumn = (
  doc: any,
  fonts: IFonts,
  group: IVisualNetworkGroup,
  side: 'left' | 'right',
  columnX: number,
  top: number,
  bottom: number,
  centerY: number,
  subjectEdgeX: number,
) => {
  const nodesTop = top + 16;
  const slots = Math.max(
    1,
    Math.floor((bottom - nodesTop + NODE_GAP) / (NODE_H + NODE_GAP)),
  );
  const { items, hidden } = fitted(group, slots);
  const rows = items.length + (hidden ? 1 : 0);
  const stackH = stackHeight(rows);
  const start = Math.min(
    Math.max(nodesTop, centerY - stackH / 2),
    bottom - stackH,
  );
  const fromX = side === 'left' ? columnX + COLUMN_W : columnX;

  // The group's name sits just above its nodes, however few there are.
  label(
    doc,
    fonts,
    groupLabel(group),
    columnX,
    start - 16,
    COLUMN_W,
    group.flaggedCount ? TONE.high.text : COLOR.label,
    side === 'left' ? 'left' : 'right',
  );
  items.forEach((item, i) =>
    connect(
      doc,
      fromX,
      start + i * (NODE_H + NODE_GAP) + NODE_H / 2,
      subjectEdgeX,
      centerY,
      itemTone(item),
    ),
  );
  items.forEach((item, i) =>
    drawNode(
      doc,
      fonts,
      item,
      columnX,
      start + i * (NODE_H + NODE_GAP),
      COLUMN_W,
    ),
  );
  if (hidden) {
    drawMore(
      doc,
      fonts,
      hidden,
      columnX,
      start + items.length * (NODE_H + NODE_GAP),
      COLUMN_W,
    );
  }
};

/** A grid of nodes under the subject, its first row wired to it. */
const renderGrid = (
  doc: any,
  fonts: IFonts,
  group: IVisualNetworkGroup,
  left: number,
  right: number,
  top: number,
  bottom: number,
  subjectX: number,
  subjectBottom: number,
) => {
  const cols = gridColumnsFor(right - left);
  const cellW = Math.min(170, (right - left - GRID_GAP * (cols - 1)) / cols);
  const gridTop = top + 22;
  const rows = Math.max(
    1,
    Math.floor((bottom - gridTop + GRID_GAP) / (NODE_H + GRID_GAP)),
  );
  const { items, hidden } = fitted(group, rows * cols);
  const cells = items.length + (hidden ? 1 : 0);
  const firstRow = Math.min(cols, cells);
  // Centre each row, so a short row sits under the subject.
  const rowStart = (count: number) =>
    left + (right - left - (count * cellW + (count - 1) * GRID_GAP)) / 2;

  for (let i = 0; i < firstRow; i += 1) {
    const cellX = rowStart(firstRow) + i * (cellW + GRID_GAP);
    connect(
      doc,
      subjectX,
      subjectBottom,
      cellX + cellW / 2,
      gridTop,
      items[i] ? itemTone(items[i]) : null,
      true,
    );
  }

  // The group's name sits over the wires, on the panel colour.
  const text = groupLabel(group).toUpperCase();
  doc.font(fonts.bold).fontSize(7);
  // characterSpacing 1 adds a point per character to the measured width.
  const textW = Math.min(
    right - left,
    doc.widthOfString(text) + text.length + 16,
  );
  const labelX = subjectX - textW / 2;
  doc.save();
  doc.rect(labelX, top, textW, 12).fill(COLOR.card);
  doc.restore();
  label(
    doc,
    fonts,
    groupLabel(group),
    labelX,
    top + 2,
    textW,
    group.flaggedCount ? TONE.high.text : COLOR.label,
    'center',
  );

  for (let i = 0; i < cells; i += 1) {
    const row = Math.floor(i / cols);
    const inRow = Math.min(cols, cells - row * cols);
    const cellX = rowStart(inRow) + (i % cols) * (cellW + GRID_GAP);
    const cellY = gridTop + row * (NODE_H + GRID_GAP);
    if (i < items.length) {
      drawNode(doc, fonts, items[i], cellX, cellY, cellW);
    } else {
      drawMore(doc, fonts, hidden, cellX, cellY, cellW);
    }
  }
};

const renderSubject = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  cx: number,
  cy: number,
) => {
  const r = SUBJECT_R;
  glow(doc, cx, cy, r * 0.8, r * 2.1, 0.3);
  doc.save();
  doc.lineWidth(2).circle(cx, cy, r).fillAndStroke(COLOR.subject, COLOR.ring);
  doc.restore();

  const nameOpts: ITextOpts = {
    width: r * 1.6,
    font: fonts.bold,
    size: 9.5,
    color: COLOR.text,
    maxLines: 3,
    align: 'center',
  };
  const nameH = measure(doc, data.entityName, nameOpts);
  const badgeH = 13;
  const top = cy - (nameH + 6 + badgeH) / 2;
  drawText(doc, data.entityName, cx - r * 0.8, top, nameOpts);

  const badge = data.network.badge;
  const tone = TONE[badge.tone];
  const badgeText = badge.label.toUpperCase();
  doc.font(fonts.bold).fontSize(6.5);
  const badgeW = Math.min(
    r * 1.8,
    doc.widthOfString(badgeText) + badgeText.length * 0.5 + 12,
  );
  box(
    doc,
    cx - badgeW / 2,
    top + nameH + 6,
    badgeW,
    badgeH,
    tone.bg,
    tone.solid,
    6.5,
  );
  drawText(doc, badgeText, cx - badgeW / 2, top + nameH + 9.3, {
    width: badgeW,
    font: fonts.bold,
    size: 6.5,
    color: tone.text,
    spacing: 0.5,
    align: 'center',
  });
};

const renderNetwork = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  x: number,
  y: number,
  w: number,
  h: number,
) => {
  const { total, flagged } = data.network;
  const labels = labelsOf(data);
  panel(
    doc,
    fonts,
    labels.networkTitle,
    `${nounOf(total, labels.networkItem)} ${labels.networkSource}${
      flagged ? ` · ${flagged.toLocaleString('en-US')} flagged` : ''
    }`,
    x,
    y,
    w,
    h,
  );

  const left = x + PANEL_PAD;
  const right = x + w - PANEL_PAD;
  const top = y + MAP_TOP;
  const bottom = y + h - PANEL_PAD;
  const cx = x + w / 2;
  const slots = slotsFor(data);
  const cols = gridColumnsFor(right - left);

  if (slots.length === 1) {
    // One group: the subject on top, the group as a grid under it, the two
    // centred together in whatever height the row gives the map.
    const need = SUBJECT_ROOM - 20 + gridNeed(slots[0], cols);
    const offset = Math.max(0, (bottom - top - need) / 2);
    const cy = top + offset + SUBJECT_R + 10;
    renderGrid(
      doc,
      fonts,
      slots[0],
      left,
      right,
      cy + SUBJECT_R + 30,
      bottom - offset,
      cx,
      cy + SUBJECT_R,
    );
    renderSubject(doc, fonts, data, cx, cy);
    return;
  }

  // Two groups flank the subject; a third goes under it as a grid, which
  // takes the room it needs and leaves the rest to the columns.
  const grid = slots.length === 3 ? gridNeed(slots[2], cols) : 0;
  const upperBottom = Math.max(top + SUBJECT_ROOM, bottom - grid);
  const cy = (top + 16 + upperBottom) / 2;
  // In a wide map the columns stay close to the subject, not at the edges.
  const reach = SUBJECT_R + COLUMN_REACH;
  const leftX = Math.max(left, cx - reach - COLUMN_W);
  const rightX = Math.min(right - COLUMN_W, cx + reach);
  renderColumn(
    doc,
    fonts,
    slots[0],
    'left',
    leftX,
    top,
    upperBottom,
    cy,
    cx - SUBJECT_R,
  );
  renderColumn(
    doc,
    fonts,
    slots[1],
    'right',
    rightX,
    top,
    upperBottom,
    cy,
    cx + SUBJECT_R,
  );
  if (slots.length === 3) {
    renderGrid(
      doc,
      fonts,
      slots[2],
      left,
      right,
      upperBottom + 26,
      bottom,
      cx,
      cy + SUBJECT_R,
    );
  }
  renderSubject(doc, fonts, data, cx, cy);
};

// ---- Sanctions exposure ----------------------------------------------------
// Beside the map: the overall verdict, every sanctioned or flagged party in
// the network with how it connects and the list it is on, the high-risk
// jurisdictions, and the report's sanctions summary when there is room.

const BOX_PAD = 14;
const PART_GAP = 16;
const EXPOSURE_ROW_H = 28;
const EXPOSURE_MAX_ROWS = 8; // rows the panel asks room for

const exposureParts = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  w: number,
) => {
  const innerW = w - 2 * PANEL_PAD;
  const tone = TONE[data.assessment.tone];
  const headlineOpts: ITextOpts = {
    width: innerW - 2 * BOX_PAD,
    font: fonts.bold,
    size: 14,
    color: COLOR.text,
    maxLines: 2,
  };
  const noteOpts: ITextOpts = {
    width: innerW - 2 * BOX_PAD,
    font: fonts.regular,
    size: 8.5,
    color: tone.text,
    maxLines: 3,
    lineGap: 1.5,
  };
  const overallH =
    BOX_PAD +
    12 +
    measure(doc, data.assessment.headline, headlineOpts) +
    5 +
    measure(doc, data.assessment.note, noteOpts) +
    BOX_PAD;

  doc.font(fonts.bold).fontSize(7.5);
  const chipRows: string[][] = [];
  let used = innerW + 1;
  data.jurisdictions.forEach((jurisdiction) => {
    const chipW = Math.min(innerW, doc.widthOfString(jurisdiction) + 18);
    if (used + chipW > innerW) {
      chipRows.push([]);
      used = 0;
    }
    chipRows[chipRows.length - 1].push(jurisdiction);
    used += chipW + 6;
  });
  const jurisdictionsH = chipRows.length ? 15 + chipRows.length * 23 - 6 : 0;

  const sanctionsOpts: ITextOpts = {
    width: innerW - 2 * BOX_PAD,
    font: fonts.regular,
    size: 8,
    color: COLOR.secondary,
    maxLines: 6,
    lineGap: 1.5,
  };
  const sanctionsH = data.sanctions
    ? BOX_PAD + 13 + measure(doc, data.sanctions, sanctionsOpts) + BOX_PAD
    : 0;

  return {
    innerW: innerW,
    tone: tone,
    headlineOpts: headlineOpts,
    noteOpts: noteOpts,
    overallH: overallH,
    chipRows: chipRows,
    jurisdictionsH: jurisdictionsH,
    sanctionsOpts: sanctionsOpts,
    sanctionsH: sanctionsH,
  };
};

const exposureListH = (data: IBeneficialOwnershipVisualData, rows: number) =>
  16 + (data.exposure.length ? rows * EXPOSURE_ROW_H : 18);

const needExposure = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  w: number,
): number => {
  const parts = exposureParts(doc, fonts, data, w);
  const rows = Math.min(data.exposure.length, EXPOSURE_MAX_ROWS);
  return (
    PANEL_HEAD +
    2 +
    parts.overallH +
    PART_GAP +
    exposureListH(data, rows) +
    (parts.jurisdictionsH ? PART_GAP + parts.jurisdictionsH : 0) +
    (parts.sanctionsH ? PART_GAP + parts.sanctionsH : 0) +
    PANEL_PAD
  );
};

const SUMMARY_MIN_LINES = 2;

/**
 * How many of `flagged` parties the list draws in `rows` rows, and how many it
 * leaves to the "+N more" line, which takes a row of its own when the list is
 * cut.
 */
export const exposureListing = (
  flagged: number,
  rows: number,
): { listed: number; hidden: number } => {
  const listed = flagged > rows ? Math.max(rows - 1, 0) : flagged;
  return { listed: listed, hidden: flagged - listed };
};

/**
 * The sanctions summary's text options cut to the lines that fit in
 * `textRoom`, or null when fewer than two would.
 */
export const sanctionsSummaryFit = (
  doc: any,
  opts: ITextOpts,
  textRoom: number,
): ITextOpts | null => {
  const lines = Math.min(
    opts.maxLines || 1,
    Math.floor(textRoom / lineHeightOf(doc, opts)),
  );
  return lines >= SUMMARY_MIN_LINES ? { ...opts, maxLines: lines } : null;
};

const renderExposure = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  x: number,
  y: number,
  w: number,
  h: number,
) => {
  const flagged = data.exposure.length;
  const labels = labelsOf(data);
  panel(
    doc,
    fonts,
    labels.exposureTitle,
    flagged ? nounOf(flagged, labels.exposureItem) : labels.exposureNone,
    x,
    y,
    w,
    h,
  );
  const parts = exposureParts(doc, fonts, data, w);
  const innerX = x + PANEL_PAD;
  const innerW = parts.innerW;
  const bottom = y + h - PANEL_PAD;
  let cy = y + PANEL_HEAD + 2;

  // The overall verdict, in a box tinted by how serious it is.
  box(
    doc,
    innerX,
    cy,
    innerW,
    parts.overallH,
    parts.tone.bg,
    parts.tone.border,
    8,
  );
  label(
    doc,
    fonts,
    'Overall assessment',
    innerX + BOX_PAD,
    cy + BOX_PAD,
    innerW - 2 * BOX_PAD,
    parts.tone.text,
  );
  const headlineH = drawText(
    doc,
    data.assessment.headline,
    innerX + BOX_PAD,
    cy + BOX_PAD + 12,
    parts.headlineOpts,
  );
  drawText(
    doc,
    data.assessment.note,
    innerX + BOX_PAD,
    cy + BOX_PAD + 17 + headlineH,
    parts.noteOpts,
  );
  cy += parts.overallH + PART_GAP;

  // The flagged parties come first, leaving room for the jurisdictions; the
  // sanctions summary takes only what is left after both (see its foot).
  const jurisdictionsRoom = parts.jurisdictionsH
    ? PART_GAP + parts.jurisdictionsH
    : 0;
  const rows = Math.min(
    flagged,
    Math.max(
      1,
      Math.floor((bottom - cy - 16 - jurisdictionsRoom) / EXPOSURE_ROW_H),
    ),
  );

  label(
    doc,
    fonts,
    `${labels.exposureList} · ${flagged.toLocaleString('en-US')}`,
    innerX,
    cy,
    innerW,
    flagged ? TONE.high.text : COLOR.label,
  );
  cy += 16;
  if (!flagged) {
    doc.save();
    doc.circle(innerX + 4, cy + 5, 3).fill(TONE.low.solid);
    doc.restore();
    cy += drawText(doc, labels.exposureEmpty, innerX + 14, cy, {
      width: innerW - 14,
      font: fonts.regular,
      size: 8.5,
      color: TONE.low.text,
      maxLines: 2,
    });
  } else {
    const { listed: listedCount, hidden } = exposureListing(flagged, rows);
    const listed = data.exposure.slice(0, listedCount);
    listed.forEach((item, i) => {
      const rowY = cy + i * EXPOSURE_ROW_H;
      const rowTone = TONE[itemTone(item) || 'high'];
      doc.save();
      doc.circle(innerX + 4, rowY + 6, 3).fill(rowTone.solid);
      doc.restore();
      drawText(doc, item.name, innerX + 14, rowY, {
        width: innerW - 14,
        font: fonts.bold,
        size: 8.5,
        color: COLOR.text,
      });
      drawText(doc, item.detail, innerX + 14, rowY + 12.5, {
        width: innerW - 14,
        font: fonts.regular,
        size: 7.5,
        color: rowTone.text,
      });
      if (i < listed.length - 1 || hidden) {
        hLine(doc, innerX, innerX + innerW, rowY + EXPOSURE_ROW_H - 4);
      }
    });
    cy += listed.length * EXPOSURE_ROW_H;
    if (hidden) {
      drawText(
        doc,
        `+${hidden.toLocaleString('en-US')} ${labels.exposureMore}`,
        innerX + 14,
        cy + 2,
        { width: innerW - 14, font: fonts.bold, size: 8, color: COLOR.accent },
      );
      cy += EXPOSURE_ROW_H;
    }
  }

  // Chips are drawn only where they fit inside the panel.
  if (parts.jurisdictionsH && cy + PART_GAP + 15 + 17 <= bottom) {
    cy += PART_GAP;
    label(doc, fonts, labels.jurisdictions, innerX, cy, innerW);
    parts.chipRows.forEach((row, r) => {
      let chipX = innerX;
      const chipY = cy + 15 + r * 23;
      if (chipY + 17 > bottom) return;
      row.forEach((jurisdiction) => {
        doc.font(fonts.bold).fontSize(7.5);
        const chipW = Math.min(innerW, doc.widthOfString(jurisdiction) + 18);
        box(doc, chipX, chipY, chipW, 17, TONE.high.bg, TONE.high.border, 8.5);
        drawText(doc, jurisdiction, chipX + 9, chipY + 4.5, {
          width: chipW - 18,
          font: fonts.bold,
          size: 7.5,
          color: TONE.high.text,
        });
        chipX += chipW + 6;
      });
    });
    cy += parts.jurisdictionsH;
  }

  // The sanctions summary sits at the foot of the panel, in the room the
  // list and jurisdictions leave: cut to fit, and left out when not even two
  // lines fit. It never runs past the panel.
  const summaryOpts = sanctionsSummaryFit(
    doc,
    parts.sanctionsOpts,
    bottom - cy - PART_GAP - 2 * BOX_PAD - 13,
  );
  if (data.sanctions && summaryOpts) {
    const summaryH =
      BOX_PAD + 13 + measure(doc, data.sanctions, summaryOpts) + BOX_PAD;
    const footY = bottom - summaryH;
    box(
      doc,
      innerX,
      footY,
      innerW,
      summaryH,
      TONE.info.bg,
      TONE.info.border,
      8,
    );
    label(
      doc,
      fonts,
      labels.summary,
      innerX + BOX_PAD,
      footY + BOX_PAD,
      innerW - 2 * BOX_PAD,
      TONE.info.text,
    );
    drawText(
      doc,
      data.sanctions,
      innerX + BOX_PAD,
      footY + BOX_PAD + 13,
      summaryOpts,
    );
  }
};

// ---- Footer ----------------------------------------------------------------

const renderFooter = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
  pageH: number,
) => {
  hLine(doc, M, PAGE_W - M, pageH - 34);
  drawText(doc, labelsOf(data).footer, M, pageH - 24, {
    width: 400,
    font: fonts.regular,
    size: 7.5,
    color: COLOR.muted,
  });
  drawText(
    doc,
    `Confidential · ${data.ticketName || data.entityName}`,
    PAGE_W - M - 600,
    pageH - 24,
    {
      width: 600,
      font: fonts.regular,
      size: 7.5,
      color: COLOR.muted,
      align: 'right',
    },
  );
};

// ---- Layout ----------------------------------------------------------------

const EXPOSURE_W = 360;
const COMPACT_W = 380;
const COMPACT_NO_FINDINGS_MAP_W = 560;

interface IMainColumns {
  network: number;
  exposure: number;
  /** A findings column beside them; 0 when findings run as the strip. */
  findings: number;
}

/**
 * How the main row is shared, chosen from what the report holds. A real
 * network leads, with the exposure beside it and the findings as a strip
 * underneath. A small one gets a compact map, and the findings take a full
 * column beside the exposure instead.
 */
export const mainColumns = (
  data: IBeneficialOwnershipVisualData,
): IMainColumns => {
  if (!isCompactNetwork(data)) {
    return {
      network: CONTENT_W - EXPOSURE_W - PANEL_GAP,
      exposure: EXPOSURE_W,
      findings: 0,
    };
  }
  if (!data.findings.length) {
    return {
      network: COMPACT_NO_FINDINGS_MAP_W,
      exposure: CONTENT_W - COMPACT_NO_FINDINGS_MAP_W - PANEL_GAP,
      findings: 0,
    };
  }
  return {
    network: COMPACT_W,
    exposure: COMPACT_W,
    findings: CONTENT_W - 2 * COMPACT_W - 2 * PANEL_GAP,
  };
};

interface ILayout {
  pageH: number;
  kpiY: number;
  mainY: number;
  mainH: number;
  findingsY: number;
  findingsH: number;
}

/** Measures every section and sizes the page to its content. */
const planLayout = (
  doc: any,
  fonts: IFonts,
  data: IBeneficialOwnershipVisualData,
): ILayout => {
  const kpiY = STRIP_Y + STRIP_H + ROW_GAP;
  const mainY = (data.kpis.length ? kpiY + KPI_H : kpiY - ROW_GAP) + PANEL_GAP;
  const columns = mainColumns(data);
  // Findings in their own column take the row's height; as a strip, their own.
  const findingsH = columns.findings ? 0 : needFindingsStrip(doc, fonts, data);
  const findingsRoom = findingsH ? PANEL_GAP + findingsH : 0;
  const need = Math.max(
    needNetwork(data, columns.network),
    needExposure(doc, fonts, data, columns.exposure),
  );
  const mainH = Math.min(
    MAX_PAGE_H - FOOTER_H - findingsRoom - mainY,
    Math.max(MIN_MAIN_H, Math.ceil(need)),
  );
  return {
    pageH: mainY + mainH + findingsRoom + FOOTER_H,
    kpiY: kpiY,
    mainY: mainY,
    mainH: mainH,
    findingsY: mainY + mainH + PANEL_GAP,
    findingsH: findingsH,
  };
};

export const beneficialOwnershipVisualPDF = async (
  data: IBeneficialOwnershipVisualData,
  name: string,
  opts: { shouldDownload?: boolean } = {},
): Promise<Blob> => {
  const [logo, regularFont, boldFont] = await Promise.all([
    loadLightLogo(),
    fetchFont('OpenSans-Regular.ttf'),
    fetchFont('OpenSans-Bold.ttf'),
  ]);

  // Measure on a scratch document first: the page is sized to its content.
  const scratch = new PDFDocument({ size: [PAGE_W, MAX_PAGE_H], margin: 0 });
  const layout = planLayout(
    scratch,
    registerFonts(scratch, regularFont, boldFont),
    data,
  );
  scratch.end();

  const pdfBlob = await new Promise<Blob>((resolve, reject) => {
    try {
      const doc = new PDFDocument({
        size: [PAGE_W, layout.pageH],
        margin: 0,
        info: {
          Title: `${labelsOf(data).documentTitle} — ${
            data.ticketName || data.entityName
          }`,
          Author: labelsOf(data).documentAuthor,
          Subject: labelsOf(data).documentTitle.replace(/^BITE /, ''),
        },
      });

      const chunks: any[] = [];
      doc.on('data', (chunk: any) => chunks.push(chunk));
      doc.on('end', () =>
        resolve(new Blob(chunks, { type: 'application/pdf' })),
      );

      const fonts = registerFonts(doc, regularFont, boldFont);
      const columns = mainColumns(data);

      renderHeader(doc, fonts, data, logo, layout.pageH);
      renderStrip(doc, fonts, data, STRIP_Y);
      if (data.kpis.length) renderKpis(doc, fonts, data, layout.kpiY);
      renderNetwork(
        doc,
        fonts,
        data,
        M,
        layout.mainY,
        columns.network,
        layout.mainH,
      );
      renderExposure(
        doc,
        fonts,
        data,
        M + columns.network + PANEL_GAP,
        layout.mainY,
        columns.exposure,
        layout.mainH,
      );
      if (columns.findings) {
        renderFindingsColumn(
          doc,
          fonts,
          data,
          PAGE_W - M - columns.findings,
          layout.mainY,
          columns.findings,
          layout.mainH,
        );
      }
      if (layout.findingsH) {
        renderFindingsStrip(
          doc,
          fonts,
          data,
          layout.findingsY,
          layout.findingsH,
        );
      }
      renderFooter(doc, fonts, data, layout.pageH);

      doc.end();
    } catch (e) {
      reject(e);
    }
  });

  const finalName = name.endsWith('.pdf') ? name : `${name}.pdf`;
  if (opts.shouldDownload ?? true) {
    const url = URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 250);
  }

  return pdfBlob;
};
